import { memo, useCallback, useEffect, useState } from "react";
import { useTranslate, classNames } from "helpers";
import { VehicleCategoryCardProps } from "./types";
import { Button } from "ui/components/Button/Button";
import MultiDropdown from "ui/components/Dropdown/MultiDropdown/MultiDropdown";
import { MultiDropdownOption } from "ui/components/Dropdown/MultiDropdown/types";
import { getFleetSettingsAPI } from "services/api/settings/fleet/api";
import { FleetSetting } from "services/api/settings/fleet/types";
import VehicleCategoryEditCard from "./components/VehicleCategoryEditCard/VehicleCategoryEditCard";
import { VEHICLE_WIZARD_STEP_LABELS } from "services/api/settings/fleet/consts";

const VehicleCategoryCard: React.FC<VehicleCategoryCardProps> = () => {
  const { t } = useTranslate();

  const [fleetSetting, setFleetSetting] = useState<FleetSetting | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    fetchFleetSettings();
  }, []);

  const fetchFleetSettings = async () => {
    try {
      const result = await getFleetSettingsAPI();
      setFleetSetting(result);
    } catch {
      return;
    }
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleEditSuccess = useCallback(() => {
    fetchFleetSettings();
    setShowEditModal(false);
  }, []);

  const categoryOptions: MultiDropdownOption[] = (fleetSetting?.vehicleCategories || []).map((c) => ({
    value: c.id,
    text: c.name,
  }));

  const selectedCategoryIds = (fleetSetting?.selectedVehicleCategories || []).map((c) => c.id);

  const vehicleSteps = fleetSetting?.vehicleSteps || [];
  const enabledStepKeys = fleetSetting?.enabledVehicleStepKeys || [];

  return (
    <>
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32">
        <div className="flex flex-col flex-1 max-w-[300px]">
          <div>
            <p className="text-base font-bold text-slate-600 py-3">
              {t("admin.settings.fleet.vehiclecategories.title")}
            </p>
            <p className="text-sm">
              {t("admin.settings.fleet.vehiclecategories.description")}
            </p>
          </div>
        </div>
        <div className="flex justify-between items-center flex-1 px-3 my-3">
          <div className="flex flex-col min-w-[500px]">
            <MultiDropdown
              name="vehicleCategoryIds"
              label={t("admin.settings.fleet.vehiclecategories.label")}
              options={categoryOptions}
              value={selectedCategoryIds}
              onChange={() => {}}
              width="full"
              disabled
            />
          </div>
          <div>
            <Button
              variant="outlined"
              children={t("admin.edit")}
              onClick={handleEditClick}
              startIcon="/media/icons/plus.svg"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32 border-t border-slate-200 mt-4 pt-4">
        <div className="flex flex-col flex-1 max-w-[300px]">
          <p className="text-base font-bold text-slate-600 py-3">
            Koraci unosa vozila
          </p>
          <p className="text-sm">
            Koraci koje korisnici prolaze pri unosu vozila.
          </p>
        </div>
        <div className="flex flex-1 px-3 my-3 flex-wrap gap-2 items-center content-start">
          {vehicleSteps.map((step) => {
            const enabled = step.required || enabledStepKeys.includes(step.key);
            return (
              <div
                key={step.key}
                className={classNames(
                  "inline-flex items-center gap-2 px-3 py-2.5 rounded-sm border text-sm",
                  enabled
                    ? "bg-sky-50 border-sky-500 text-sky-700"
                    : "bg-white border-slate-200 text-slate-500"
                )}
              >
                <span
                  className={classNames(
                    "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0",
                    enabled
                      ? "bg-sky-600 border-sky-600 text-white"
                      : "border-slate-300 bg-white"
                  )}
                >
                  {enabled && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                {VEHICLE_WIZARD_STEP_LABELS[step.key] ?? step.key}
              </div>
            );
          })}
        </div>
      </div>

      <VehicleCategoryEditCard
        show={showEditModal}
        categoryOptions={categoryOptions}
        selectedCategoryIds={selectedCategoryIds}
        vehicleSteps={vehicleSteps}
        enabledVehicleStepKeys={enabledStepKeys}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEditModal(false)}
      />
    </>
  );
};

export default memo(VehicleCategoryCard);
